import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react/macro';
import '@warp-ds/components/icon';
import { Button } from '@warp-ds/react';
import { useEffect, useRef } from 'react';
import { getHuckConfig } from '../../../../utils/getHuckConfig.js';
import type { UploadSlot } from '../types/index.js';

interface SortableImageProps {
  slot: UploadSlot & { onEditDescription?: () => void; onRotate?: () => Promise<void>; isRotating?: boolean };
  onDelete: (key: string) => void;
  isDraggable?: boolean;
  isDragOver?: boolean;
  onEditDescription?: () => void;
  testid?: string;
  disabled?: boolean;
  imageIndex: number;
}

export function SortableImage({
  slot,
  onDelete,
  isDraggable = true,
  isDragOver = false,
  onEditDescription,
  testid,
  disabled = false,
  imageIndex,
}: SortableImageProps) {
  const config = getHuckConfig();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: slot.key,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    aspectRatio: '4 / 3',
    position: isDragging ? 'relative' : undefined,
    userSelect: 'none' as const,
  } satisfies React.CSSProperties;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Remove focus from any element in this image when dragging ends
    if (!isDragging && containerRef.current && document.activeElement) {
      if (containerRef.current.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
    }
  }, [slot.key, isDragging]);

  return (
    <div className="relative w-full h-auto min-h-0 min-w-0 group" data-testid={testid}>
      <div
        ref={setNodeRef}
        style={style}
        className={`w-full aspect-4/3 min-h-0 min-w-0${disabled ? ' opacity-60' : ''}`}
      >
        <div
          ref={containerRef}
          className={`group relative w-full h-full pointer-events-auto rounded-4 outline-none ${!isDragging ? 'focus-within:outline-3 focus-within:s-outline-focus focus-within:[--w-outline-offset:-2px]' : ''} ${
            isDraggable ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
          }`}
          style={{ touchAction: 'manipulation' }}
          {...(isDraggable ? { ...listeners } : {})}
        >
          {/* Visually hidden drag handle for keyboard/screen reader access */}
          {isDraggable && (
            <button
              type="button"
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              className="sr-only"
              aria-label={i18n.t({
                id: 'mobility-item-insertion.form.imageUpload.dragHandleAriaLabel',
                message: 'Drag to reorder photo',
                comment: 'Aria label for drag handle button',
              })}
            />
          )}
          <div className="relative rounded-4 overflow-hidden aspect-3/2 flex-1">
            {slot.status === 'done' && slot.uploadedFile ? (
              <>
                {/* Background blurred image */}
                <img
                  aria-hidden="true"
                  src={`${config.cdnUrl}/1280w/${slot.uploadedFile.path}`}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    filter: 'blur(20px)',
                    zIndex: 1,
                  }}
                  draggable={false}
                />
                {/* Foreground actual image */}
                <img
                  src={`${config.cdnUrl}/1280w/${slot.uploadedFile.path}`}
                  alt={
                    slot.description
                      ? `${imageIndex + 1}: ${slot.description}`
                      : i18n.t({
                          id: 'mobility-item-insertion.form.imageUpload.defaultAltText',
                          values: { number: imageIndex + 1 },
                          message: '{number}: Uploaded image',
                          comment: 'Default alt text for uploaded images when no description is provided',
                        })
                  }
                  className="relative w-full h-full object-contain transition-opacity duration-200 ease-in-out"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    zIndex: 2,
                  }}
                  draggable={false}
                />
                {/* Hover/focus overlay */}
                <div
                  className={`pointer-events-none absolute inset-0 transition-opacity rounded-4 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 ${isDragging || isDragOver ? '!opacity-0' : ''}`}
                  style={{ background: 'rgba(0,0,0,0.30)', zIndex: 3 }}
                />
                {/* Overlay with buttons - placed in the image container */}
                <div
                  className={`flex flex-col justify-center items-start pl-8 transition-opacity image-overlay-actions opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 ${isDragging || isDragOver ? '!opacity-0 !pointer-events-none' : ''}`}
                  style={{ zIndex: 4 }}
                >
                  <div className="flex flex-col gap-8">
                    {/* Delete button */}
                    <Button
                      type="button"
                      utility
                      quiet
                      negative
                      onClick={() => {
                        onDelete(slot.key);
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      className="hover:bg-transparent text-detail"
                      aria-label={i18n.t({
                        id: 'mobility-item-insertion.form.imageUpload.deletePhotoAriaLabel',
                        message: 'Delete photo',
                        comment: 'Aria label for delete photo button',
                      })}
                      style={{ padding: 0 }}
                    >
                      <div
                        className="flex items-center gap-8 s-text-inverted rounded-4 p-4 font-normal"
                        style={{ background: 'rgba(0,0,0,0.60)' }}
                      >
                        <w-icon name="Bin" size="small" className="s-icon-inverted" locale={config.locale} />
                        <Trans id="mobility-item-insertion.form.imageUpload.deletePhoto" comment="Delete photo">
                          Delete
                        </Trans>
                      </div>
                    </Button>
                    {/* Rotate button */}
                    {slot.status === 'done' && (
                      <Button
                        type="button"
                        utility
                        quiet
                        onClick={async () => {
                          if (slot.isRotating) return;
                          if (!slot.onRotate) return;
                          await slot.onRotate();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        className="hover:bg-transparent text-detail"
                        aria-label={i18n.t({
                          id: 'mobility-item-insertion.form.imageUpload.rotatePhotoAriaLabel',
                          message: 'Rotate photo',
                          comment: 'Aria label for rotate photo button',
                        })}
                        style={{ padding: 0 }}
                        disabled={slot.isRotating}
                      >
                        <div
                          className="flex items-center gap-8 s-text-inverted rounded-4 p-4 font-normal"
                          style={{ background: 'rgba(0,0,0,0.60)' }}
                        >
                          {slot.isRotating ? (
                            <div
                              className="animate-spinner"
                              style={{ '--spinner-size': '16px' } as React.CSSProperties}
                            />
                          ) : (
                            <w-icon name="Refresh" size="small" className="s-icon-inverted" locale={config.locale} />
                          )}
                          <Trans id="mobility-item-insertion.form.imageUpload.rotatePhoto" comment="Rotate photo">
                            Rotate
                          </Trans>
                        </div>
                      </Button>
                    )}
                    {/* Edit description button */}
                    {onEditDescription && (
                      <Button
                        type="button"
                        utility
                        quiet
                        onClick={() => {
                          onEditDescription();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        className="hover:bg-transparent text-detail"
                        aria-label={i18n.t({
                          id: 'mobility-item-insertion.form.imageUpload.editDescriptionAriaLabel',
                          message: 'Edit photo description',
                          comment: 'Aria label for edit photo description button',
                        })}
                        style={{ padding: 0 }}
                      >
                        <div
                          className="flex items-center gap-8 s-text-inverted rounded-4 p-4 font-normal"
                          style={{ background: 'rgba(0,0,0,0.60)' }}
                        >
                          <w-icon name="Edit" size="small" className="s-icon-inverted" locale={config.locale} />
                          <Trans
                            id="mobility-item-insertion.form.imageUpload.editDescription"
                            comment="Edit photo description"
                          >
                            Image text
                          </Trans>
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
              </>
            ) : slot.status === 'uploading' && slot.previewUrl ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center s-bg-neutral-subtle">
                <img
                  src={slot.previewUrl}
                  alt={i18n.t({
                    id: 'mobility-item-insertion.form.imageUpload.uploadingPreviewAlt',
                    message: 'Uploading preview',
                    comment: 'Alt text for image preview while uploading',
                  })}
                  className="w-full h-full object-cover rounded-4 opacity-60"
                />
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    backdropFilter: 'blur(0.8px)',
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: 'inherit',
                  }}
                >
                  <div className="animate-spinner" style={{ '--spinner-size': '24px' } as React.CSSProperties} />
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center s-bg-neutral-subtle animate-pulse">
                <div className="w-8 h-8 border-4 s-border-info border-t-transparent rounded-full animate-spin mb-2" />
                <span className="text-xs s-text-neutral">Uploading…</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {slot.description && !isDragging && (
        <div className="w-full text-xs break-words truncate mt-[-12px]">{slot.description}</div>
      )}
    </div>
  );
}
